/**
 * 极简 CSV 导出工具。BOM + utf-8,Excel 中文不乱码。
 * 用法:
 *   downloadCsv('signals', items, [
 *     { key: 'createdAt', header: '时间' },
 *     { key: 'employeeAccountId', header: '雇员账户' },
 *     { key: 'employeeVolume', header: '手数', format: (v) => (Number(v) / 100).toFixed(2) },
 *   ])
 */
export interface CsvColumn<T> {
  key: keyof T | string;
  header: string;
  format?: (val: unknown, row: T) => string;
}

export interface CsvOptions {
  /**
   * 口径说明,作为**首行的单个单元格**写在表头之前(不属于表头)。
   *
   * 代价:表头落到第 2 行,Excel 自动筛选 / Power Query / pandas 都要手动指定标题行。
   * 因此**默认不加**,只给「文件会脱离页面单独流转、口径写不清就会算错钱」的导出用(如月度分润:
   * 同一个月份可以导出"默认口径"与"全量"两份,列一模一样、合计不同,不写清楚事后分不出谁是谁)。
   * 其余页面照旧不传,保持标准单表头 CSV。
   */
  note?: string;
}

export function downloadCsv<T>(filename: string, rows: T[], columns: CsvColumn<T>[], opts?: CsvOptions): void {
  // CSV/公式注入防护:以 = + - @ Tab CR 开头的单元格,Excel/WPS 打开会当公式执行。
  // 先前置一个单引号让其退化为纯文本,再做引号转义。正常文本(不以这些字符开头)不受影响。
  const neutralizeFormula = (s: string) => (/^[=+\-@\t\r]/.test(s) ? `'${s}` : s);
  const escape = (s: string) => {
    const safe = neutralizeFormula(s);
    return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };

  const headerLine = columns.map((c) => escape(c.header)).join(',');
  const bodyLines = rows.map((row) =>
    columns
      .map((c) => {
        const raw = (row as Record<string, unknown>)[c.key as string];
        const val = c.format ? c.format(raw, row) : raw == null ? '' : String(raw);
        return escape(val);
      })
      .join(','),
  );

  // 说明行同样走 escape:它含中文标点与括号,一旦出现逗号/引号必须整体包成一个单元格,
  // 否则会被 Excel 拆成多列、把整张表的列宽推歪。
  const csv = [...(opts?.note ? [escape(opts.note)] : []), headerLine, ...bodyLines].join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
